import { ItemSettingsService } from '@supply-mover-2-0/core';
import React, { useEffect, useRef, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { editEmptiesMaskedValue } from './maskedInputEdits';

type MaskType = 'currency' | 'percent' | 'number';

interface MaskConfig {
	prefix: string;
	suffix: string;
	minDecimals: number;
	maxDecimals: number;
	thousandSeparator: boolean;
}

const DEFAULT_MASK_CONFIGS: Record<MaskType, MaskConfig> = {
	currency: {
		prefix: '$',
		suffix: '',
		minDecimals: 2,
		maxDecimals: 2,
		thousandSeparator: true,
	},
	percent: {
		prefix: '',
		suffix: '%',
		minDecimals: 0,
		maxDecimals: 0,
		thousandSeparator: false,
	},
	number: {
		prefix: '',
		suffix: '',
		minDecimals: 0,
		maxDecimals: 2,
		thousandSeparator: true,
	},
};

interface MaskedInputProps {
	value: number | null;
	onChange: (value: number) => void;
	/**
	 * PD-13793: fired (in `nullable` mode only) when the user clears the field
	 * to empty. An empty field means "no value" (null), which is semantically
	 * distinct from an entered 0 — so a cleared field calls onClear instead of
	 * onChange(0), letting the parent persist null. Non-nullable inputs and any
	 * caller that doesn't pass it are unaffected.
	 */
	onClear?: () => void;
	mask: MaskType;
	prefix?: string;
	suffix?: string;
	minDecimals?: number;
	maxDecimals?: number;
	thousandSeparator?: boolean;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	disabled?: boolean;
	placeholder?: string;
	className?: string;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	fullWidth?: boolean;
	error?: boolean | string;
	// When true, a null value renders as an empty input (with placeholder visible)
	// instead of formatting 0 (e.g., "$0.00"). Once the user types, the input behaves
	// normally. With `onClear`, removing every digit returns it to empty and calls
	// onClear (see also `emptyOnDeleteToZero`). Without `onClear` it falls back to
	// the formatted zero.
	nullable?: boolean;
	// PD-16171 — with `nullable` and `onClear`, a deletion that leaves only the
	// formatted zeros (backspacing the last real digit out) empties the field as
	// well, so an emptied field and a typed $0.00 stay distinct. Off by default,
	// where only removing every digit (a select-all delete) empties it.
	emptyOnDeleteToZero?: boolean;
}

/**
 * Formats a number for display with the given configuration
 */
const formatForDisplay = (value: number, config: MaskConfig): string => {
	const { prefix, suffix, minDecimals, maxDecimals, thousandSeparator } =
		config;

	// Ensure minDecimals doesn't exceed maxDecimals
	const effectiveMinDecimals = Math.min(minDecimals, maxDecimals);

	const formatted = thousandSeparator
		? value.toLocaleString('en-US', {
				minimumFractionDigits: effectiveMinDecimals,
				maximumFractionDigits: maxDecimals,
			})
		: value.toFixed(maxDecimals);

	return `${prefix}${formatted}${suffix}`;
};

/**
 * Converts a raw integer value to the display value based on decimal places.
 * e.g., with maxDecimals=2: 12345 -> 123.45
 * e.g., with maxDecimals=0: 50 -> 50
 */
const rawToValue = (raw: number, maxDecimals: number): number => {
	if (maxDecimals === 0) {
		return raw;
	}
	const divisor = Math.pow(10, maxDecimals);
	return Number((raw / divisor).toFixed(maxDecimals));
};

/**
 * Converts a display value to raw integer based on decimal places.
 * e.g., with maxDecimals=2: 123.45 -> 12345
 * e.g., with maxDecimals=0: 50 -> 50
 */
const valueToRaw = (value: number, maxDecimals: number): number => {
	if (maxDecimals === 0) {
		return Math.round(value);
	}
	const multiplier = Math.pow(10, maxDecimals);
	return Math.round(value * multiplier);
};

/**
 * Parses a string input into a raw integer value (like cents for currency).
 * Strips all non-digits and interprets as the smallest unit.
 */
const parseInputToRaw = (text: string): number => {
	// Extract only digits from input
	const digits = text.replace(/\D/g, '');
	const parsed = parseInt(digits, 10);
	return isNaN(parsed) ? 0 : parsed;
};

/**
 * A flexible masked input component that handles currency, percent, and number formatting.
 * Displays formatted values while storing raw numbers.
 */
export const MaskedInput = React.memo(
	({
		value,
		onChange,
		onClear,
		mask,
		prefix,
		suffix,
		minDecimals,
		maxDecimals,
		thousandSeparator,
		onKeyDown,
		onBlur,
		disabled = false,
		placeholder,
		className = '',
		size = 'md',
		fullWidth = false,
		error = false,
		nullable = false,
		emptyOnDeleteToZero = false,
	}: MaskedInputProps): React.ReactElement => {
		const colors = useThemeColors();
		const inputRef = useRef<HTMLInputElement>(null);
		const shouldSelectAllRef = useRef<boolean>(false);

		// Build the effective config by merging defaults with overrides.
		// For currency mask, use the global price decimal precision as the default
		// for both minDecimals and maxDecimals so that empty/zero values display
		// the correct number of decimal places (e.g., "$0.0000" instead of "$0.00").
		const defaultConfig = DEFAULT_MASK_CONFIGS[mask];
		const pricePrecision =
			mask === 'currency'
				? ItemSettingsService.getMaxPriceDecimalPrecision()
				: null;
		const config: MaskConfig = {
			prefix: prefix ?? defaultConfig.prefix,
			suffix: suffix ?? defaultConfig.suffix,
			minDecimals: minDecimals ?? pricePrecision ?? defaultConfig.minDecimals,
			maxDecimals: maxDecimals ?? pricePrecision ?? defaultConfig.maxDecimals,
			thousandSeparator: thousandSeparator ?? defaultConfig.thousandSeparator,
		};

		// Convert external value to raw (e.g., 123.45 -> 12345 for currency)
		const externalRaw = valueToRaw(value ?? 0, config.maxDecimals);

		// Track internal raw value to allow smooth typing
		const [displayRaw, setDisplayRaw] = useState<number>(externalRaw);

		// In nullable mode, render the input as empty (placeholder visible) until the
		// user types something. Resync to empty when the parent passes null again.
		const [showEmpty, setShowEmpty] = useState<boolean>(
			nullable && value == null,
		);

		// Sync display value when external value changes
		useEffect(() => {
			setDisplayRaw(externalRaw);
		}, [externalRaw]);

		// A field that stops being nullable must show its value again. The shop
		// price input is nullable only while its item has no price (PD-16171);
		// when a price arrives after the first render (a contract price loaded
		// later, a line priced by the rep), keeping the empty state would leave
		// the field blank behind the default "$0.00" placeholder.
		useEffect(() => {
			setShowEmpty(nullable && value == null);
		}, [value, nullable]);

		// Calculate the maximum cursor position (before suffix)
		const getMaxCursorPosition = (formatted: string): number => {
			return formatted.length - config.suffix.length;
		};

		// Clamp cursor position to stay before the suffix
		const clampCursorPosition = (formatted: string): void => {
			const input = inputRef.current;
			if (input == null || config.suffix === '') return;

			const maxPos = getMaxCursorPosition(formatted);
			const start = input.selectionStart ?? 0;
			const end = input.selectionEnd ?? 0;

			if (start > maxPos || end > maxPos) {
				const newStart = Math.min(start, maxPos);
				const newEnd = Math.min(end, maxPos);
				// Use requestAnimationFrame to ensure DOM is updated
				requestAnimationFrame(() => {
					input.setSelectionRange(newStart, newEnd);
				});
			}
		};

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
			const text = e.target.value;
			const newDigits = text.replace(/\D/g, '');
			const prevDigits = displayRaw.toString();

			// PD-13793: in nullable mode, clearing all digits means "no value"
			// (null) — distinct from an entered 0. Surface it via onClear so the
			// parent persists null instead of 0, and return to the empty display.
			// PD-16171: with emptyOnDeleteToZero, backspacing down to the
			// formatted zeros clears it too, while a typed 0 stays a value.
			const nativeInputType = (e.nativeEvent as Partial<InputEvent>).inputType;
			const empties = emptyOnDeleteToZero
				? editEmptiesMaskedValue(newDigits, nativeInputType)
				: newDigits === '';

			if (nullable && onClear != null && empties) {
				setDisplayRaw(0);
				setShowEmpty(true);
				onClear();
				return;
			}

			// Compute current formatted value for comparison
			const currentDisplayValue = rawToValue(displayRaw, config.maxDecimals);
			const currentFormatted = formatForDisplay(currentDisplayValue, config);

			let newRaw: number;

			if (showEmpty) {
				// Coming out of empty state on first keystroke — interpret as fresh input
				newRaw = parseInt(newDigits, 10) || 0;
			} else if (newDigits.length < prevDigits.length) {
				// User deleted actual digit(s) - use the new digits
				newRaw = parseInt(newDigits, 10) || 0;
			} else if (
				newDigits === prevDigits &&
				text.length < currentFormatted.length
			) {
				// Same digits but shorter text = deleted formatting character (like %)
				// Treat as backspace: remove last digit
				newRaw = Math.floor(displayRaw / 10);
			} else {
				// Normal input (digit added or no change)
				newRaw = parseInt(newDigits, 10) || 0;
			}

			setDisplayRaw(newRaw);
			if (showEmpty) {
				setShowEmpty(false);
			}

			// Convert raw back to value and notify parent
			const newValue = rawToValue(newRaw, config.maxDecimals);
			onChange(newValue);

			// Clamp cursor position after the value updates
			const newFormatted = formatForDisplay(newValue, config);
			clampCursorPosition(newFormatted);
		};

		// Handle selection changes to keep cursor before suffix
		const handleSelect = (): void => {
			const displayValue = rawToValue(displayRaw, config.maxDecimals);
			const formatted = formatForDisplay(displayValue, config);
			clampCursorPosition(formatted);
		};

		// Handle keydown to manage Delete key behavior for prefix/suffix
		const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
			if (e.key === 'Delete') {
				const input = inputRef.current;
				if (input != null) {
					const displayValue = rawToValue(displayRaw, config.maxDecimals);
					const formatted = formatForDisplay(displayValue, config);
					const cursorPos = input.selectionStart ?? 0;
					const selectionEnd = input.selectionEnd ?? 0;

					// If cursor is within or before the prefix, Delete should remove the first digit
					if (
						config.prefix !== '' &&
						cursorPos <= config.prefix.length &&
						cursorPos === selectionEnd
					) {
						e.preventDefault();
						// Remove the first digit by converting raw to string, removing first char, parsing back
						const rawStr = displayRaw.toString();
						if (rawStr.length !== 0) {
							const remaining = rawStr.slice(1);
							const parsed = parseInt(remaining, 10);
							const newRaw = isNaN(parsed) ? 0 : parsed;

							// PD-13793: deleting the last digit empties a nullable field —
							// treat as cleared (null), matching the backspace path above.
							if (nullable && onClear != null && remaining === '') {
								setDisplayRaw(0);
								setShowEmpty(true);
								onClear();
								requestAnimationFrame(() => {
									input.setSelectionRange(
										config.prefix.length,
										config.prefix.length,
									);
								});
								return;
							}

							setDisplayRaw(newRaw);
							const newValue = rawToValue(newRaw, config.maxDecimals);
							onChange(newValue);
							// Keep cursor at the prefix position
							requestAnimationFrame(() => {
								input.setSelectionRange(
									config.prefix.length,
									config.prefix.length,
								);
							});
						}
						return;
					}

					// If cursor is at max position (right before suffix), prevent Delete
					if (config.suffix !== '') {
						const maxPos = getMaxCursorPosition(formatted);
						if (cursorPos === maxPos && selectionEnd === maxPos) {
							e.preventDefault();
							return;
						}
					}
				}
			}

			// Call the user's onKeyDown handler if provided
			onKeyDown?.(e);
		};

		const selectAll = (input: HTMLInputElement): void => {
			input.setSelectionRange(0, input.value.length);
		};

		/**
		 * PD-15162 — select the whole amount SYNCHRONOUSLY on focus.
		 *
		 * This used to defer the selection to a 10ms timer, which left a window
		 * where the field looked ready to be typed over but was not: a keystroke
		 * that landed first was inserted rather than replacing, so the typed
		 * digits appended to the existing amount. On the refund dialog — which
		 * pre-fills the full refundable amount — clicking the field and typing
		 * straight away turned $20.60 into $206,012.34. An amount over the
		 * refundable maximum is rejected, so the dangerous case is the one that
		 * still lands underneath it and refunds a figure nobody chose.
		 *
		 * The mouse handlers below keep the pointer from collapsing that selection
		 * again; they no longer own the selection itself.
		 */
		const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
			shouldSelectAllRef.current = true;
			selectAll(e.target);
		};

		const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>): void => {
			shouldSelectAllRef.current = document.activeElement !== e.currentTarget;
		};

		const handleMouseUp = (e: React.MouseEvent<HTMLInputElement>): void => {
			if (shouldSelectAllRef.current) {
				// Without this the mouseup collapses the selection made on focus and
				// drops the caret wherever the pointer happened to land.
				e.preventDefault();
				selectAll(e.currentTarget);
				shouldSelectAllRef.current = false;
			}
		};

		const handleClick = (e: React.MouseEvent<HTMLInputElement>): void => {
			if (shouldSelectAllRef.current) {
				e.preventDefault();
			}
		};

		// Convert raw to value and format for display
		const displayValue = rawToValue(displayRaw, config.maxDecimals);
		const formattedValue = formatForDisplay(displayValue, config);

		// Generate placeholder if not provided
		const effectivePlaceholder = placeholder ?? formatForDisplay(0, config);

		// Size configurations
		const sizeConfig = {
			xs: {
				text: 'text-xs',
				padding: 'px-2 py-1',
				height: 28,
			},
			sm: {
				text: 'text-sm',
				padding: 'px-2 py-1.5',
				height: 32,
			},
			md: {
				text: 'text-sm',
				padding: 'px-3 py-2',
				height: 36,
			},
			lg: {
				text: 'text-base',
				padding: 'px-3 py-2.5',
				height: 42,
			},
		};
		const sizeStyles = sizeConfig[size] ?? sizeConfig.md;

		const hasError =
			error === true || (typeof error === 'string' && error !== '');

		return (
			<input
				ref={inputRef}
				type="text"
				inputMode="numeric"
				value={showEmpty ? '' : formattedValue}
				onChange={handleChange}
				onFocus={handleFocus}
				onSelect={handleSelect}
				onBlur={onBlur}
				onKeyDown={handleKeyDown}
				onMouseDown={handleMouseDown}
				onMouseUp={handleMouseUp}
				onClick={handleClick}
				className={cn(
					'block rounded-md border outline-none w-full',
					sizeStyles.text,
					sizeStyles.padding,
					colors.classes.formFieldBase,
					hasError
						? colors.classes.formFieldError
						: colors.classes.formFieldDefault,
					colors.classes.formFieldFocus,
					disabled && colors.classes.formFieldDisabled,
					className,
				)}
				style={{ height: sizeStyles.height }}
				placeholder={effectivePlaceholder}
				disabled={disabled}
			/>
		);
	},
	(prevProps, nextProps) =>
		prevProps.value === nextProps.value &&
		prevProps.onChange === nextProps.onChange &&
		prevProps.onClear === nextProps.onClear &&
		prevProps.mask === nextProps.mask &&
		prevProps.prefix === nextProps.prefix &&
		prevProps.suffix === nextProps.suffix &&
		prevProps.minDecimals === nextProps.minDecimals &&
		prevProps.maxDecimals === nextProps.maxDecimals &&
		prevProps.thousandSeparator === nextProps.thousandSeparator &&
		prevProps.onKeyDown === nextProps.onKeyDown &&
		prevProps.onBlur === nextProps.onBlur &&
		prevProps.disabled === nextProps.disabled &&
		prevProps.placeholder === nextProps.placeholder &&
		prevProps.className === nextProps.className &&
		prevProps.size === nextProps.size &&
		prevProps.fullWidth === nextProps.fullWidth &&
		prevProps.error === nextProps.error &&
		prevProps.nullable === nextProps.nullable &&
		prevProps.emptyOnDeleteToZero === nextProps.emptyOnDeleteToZero,
);

MaskedInput.displayName = 'MaskedInput';
